// Handy-Erfassung: Foto/Dokument/Beleg → (teil-)automatische Einordnung
// mit Freigabe. Ablauf: Aufnahme → [KI-Vorschlag ODER manuell] → Freigabe-
// Formular (alles änderbar) → Ablage in der Firmen-Akte.

import React, { useEffect, useRef, useState } from 'react';
import { useMandantStore } from '../store/mandantStore';
import {
  prepareAnhang, classifyCapture, saveCapture,
  type CaptureAnhang, type KlassifizierungsVorschlag,
} from '../services/captureService';
import { isAnthropicConfigured, extractDocumentDataFromBase64 } from '../services/documentImportService';
import { useHandoffStore } from '../store/handoffStore';
import { toMandantID } from '../types/branded';

type Schritt = 'aufnahme' | 'wahl' | 'analysiere' | 'freigabe' | 'fertig';

export function CaptureModal({ onClose, onSaved }: {
  onClose: () => void;
  onSaved?: () => void;
}): React.JSX.Element {
  const mandanten      = useMandantStore(s => s.mandanten);
  const aktiverMandant = useMandantStore(s => s.aktiverMandant);
  const requestHandoff = useHandoffStore(s => s.request);
  const fileRef = useRef<HTMLInputElement>(null);

  const [schritt, setSchritt] = useState<Schritt>('aufnahme');
  const [anhang,  setAnhang]  = useState<CaptureAnhang | null>(null);
  const [error,   setError]   = useState('');
  const [kiWar,   setKiWar]   = useState(false);   // kam der Vorschlag von der KI?

  // Freigabe-Formular
  const [typ,      setTyp]      = useState<'BELEG' | 'DOKUMENT'>('BELEG');
  const [mandantId, setMandantId] = useState<string>(aktiverMandant ?? '');
  const [titel,    setTitel]    = useState('');
  const [datum,    setDatum]    = useState(new Date().toISOString().slice(0, 10));
  const [inhalt,   setInhalt]   = useState('');
  const [schritt2, setSchritt2] = useState('');   // vorgeschlagene Folge-Aktion
  const [betrag,   setBetrag]   = useState('');    // Euro-String, nur bei Beleg
  const [saving,   setSaving]   = useState(false);

  /* Kamera/Dateiauswahl direkt beim Öffnen anstoßen */
  useEffect(() => { fileRef.current?.click(); }, []);

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) { onClose(); return; }
    setError('');
    try {
      const a = await prepareAnhang(file);
      setAnhang(a);
      setSchritt('wahl');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSchritt('wahl'); // Fehlermeldung anzeigen, erneut versuchen möglich
    }
  };

  const handleKI = async (): Promise<void> => {
    if (anhang === null) return;
    setSchritt('analysiere');
    setError('');
    try {
      const v: KlassifizierungsVorschlag = await classifyCapture(anhang);
      setTyp(v.typ);
      if (v.mandant_id !== null) setMandantId(v.mandant_id);
      setTitel(v.titel);
      if (v.datum !== null) setDatum(v.datum);
      setInhalt(v.zusammenfassung);
      setSchritt2(v.naechster_schritt);
      setBetrag(v.betrag_cents !== null ? (v.betrag_cents / 100).toFixed(2).replace('.', ',') : '');
      setKiWar(true);
      setSchritt('freigabe');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSchritt('wahl');
    }
  };

  const handleManuell = (): void => {
    setKiWar(false);
    setSchritt2('');
    setSchritt('freigabe');
  };

  const parseBetragCents = (): number | null => {
    const norm = betrag.replace(/\s|€/g, '').replace(',', '.');
    const v = parseFloat(norm);
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
  };

  const handleSave = async (): Promise<void> => {
    if (anhang === null || mandantId === '' || titel.trim() === '') return;
    setSaving(true);
    try {
      await saveCapture({
        mandant_id: toMandantID(mandantId),
        typ,
        titel: titel.trim(),
        datum,
        inhalt,
        anhang,
        naechster_schritt: schritt2,
        betrag_cents: typ === 'BELEG' ? parseBetragCents() : null,
      });
      setSchritt('fertig');
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  /* Folge-Aktion: Formular vorbefüllt öffnen (wechselt das Modul). */
  const öffneFormular = (formularTyp: string): void => {
    requestHandoff('formulare', {
      action: 'formular',
      formularTyp,
      quelle: { datum, text: inhalt, titel },
    });
    onClose();
  };

  /* Folge-Aktion: neue Begehung anlegen (ersetzt das alte Formular). */
  const öffneBegehung = (): void => {
    requestHandoff('begehung', { action: 'begehung', quelle: { text: inhalt, titel } });
    onClose();
  };

  /* Folge-Aktion: aus dem Dokument Stammdaten anlegen (Mandant/Mitarbeiter). */
  const [stammBusy, setStammBusy] = useState<'mandant' | 'mitarbeiter' | null>(null);
  const alsMandant = async (): Promise<void> => {
    if (anhang === null) return;
    setStammBusy('mandant');
    setError('');
    try {
      const data = await extractDocumentDataFromBase64(anhang.b64, anhang.mime);
      requestHandoff('einstellungen', { action: 'mandant', data: data as unknown as Record<string, unknown> });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStammBusy(null);
    }
  };
  const alsMitarbeiter = async (): Promise<void> => {
    if (anhang === null) return;
    setStammBusy('mitarbeiter');
    setError('');
    try {
      const data = await extractDocumentDataFromBase64(anhang.b64, anhang.mime);
      /* person_geburtsdatum kommt als DD.MM.YYYY → in ISO wandeln. */
      const geb = data.person_geburtsdatum;
      let iso = '';
      if (geb !== undefined && geb !== null) {
        const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(geb);
        iso = m !== null ? `${m[3]}-${m[2]}-${m[1]}` : geb;
      }
      requestHandoff('vorsorge', {
        action: 'mitarbeiter',
        data: {
          vorname: data.person_vorname ?? '',
          nachname: data.person_nachname ?? '',
          geburtsdatum: iso,
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStammBusy(null);
    }
  };

  const vorschau = anhang !== null && anhang.mime.startsWith('image/')
    ? `data:${anhang.mime};base64,${anhang.b64}`
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl
                      max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">📷 Foto / Dokument / Beleg</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg px-1">✕</button>
        </div>

        {/* verstecktes Datei-Input mit Kamera-Aufruf */}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
          className="hidden"
          onChange={e => { void handleFile(e.target.files?.[0]); }} />

        {error !== '' && (
          <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg p-2.5">✕ {error}</p>
        )}

        {schritt === 'aufnahme' && (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-gray-500">Kamera/Dateiauswahl öffnet sich…</p>
            <button onClick={() => { fileRef.current?.click(); }} className="btn-primary">
              Aufnahme starten
            </button>
          </div>
        )}

        {vorschau !== null && schritt !== 'aufnahme' && (
          <img src={vorschau} alt="Aufnahme"
            className="w-full max-h-52 object-contain rounded-lg border border-gray-200 dark:border-gray-800" />
        )}
        {anhang !== null && anhang.mime === 'application/pdf' && (
          <p className="text-sm text-gray-500 text-center">📄 PDF-Dokument geladen</p>
        )}

        {schritt === 'wahl' && anhang !== null && (
          <div className="space-y-3">
            {isAnthropicConfigured() ? (
              <>
                <button onClick={() => { void handleKI(); }} className="btn-primary w-full justify-center">
                  🤖 Automatisch einordnen (KI)
                </button>
                <p className="text-xs text-gray-400">
                  ⚠ Nur für <strong>administrative</strong> Dokumente (Belege, Rechnungen,
                  Visitenkarten, Begehungsfotos). Keine Befunde oder Patientendaten an
                  die KI senden — dafür „Manuell einordnen" verwenden.
                </p>
              </>
            ) : (
              <p className="text-xs text-gray-400">
                KI-Einordnung nicht verfügbar (kein API-Key hinterlegt) — manuell einordnen.
              </p>
            )}
            <button onClick={handleManuell} className="btn-secondary w-full justify-center">
              ✍ Manuell einordnen
            </button>
            <button onClick={() => { fileRef.current?.click(); }}
              className="text-sm text-gray-500 hover:text-gray-700 w-full text-center">
              ↻ Andere Aufnahme
            </button>
          </div>
        )}

        {schritt === 'analysiere' && (
          <div className="text-center py-6 space-y-3">
            <div className="w-8 h-8 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-gray-500">KI analysiert das Dokument…</p>
          </div>
        )}

        {schritt === 'freigabe' && (
          <div className="space-y-3">
            {kiWar && (
              <p className="text-xs bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 rounded-lg p-2.5">
                🤖 KI-Vorschlag — bitte prüfen und bei Bedarf korrigieren, dann freigeben.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="label-text">Art</label>
                <select value={typ} onChange={e => { setTyp(e.target.value as 'BELEG' | 'DOKUMENT'); }}
                  className="input-field text-sm">
                  <option value="BELEG">🧾 Beleg</option>
                  <option value="DOKUMENT">📄 Dokument</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="label-text">Datum</label>
                <input type="date" value={datum} onChange={e => { setDatum(e.target.value); }}
                  className="input-field text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="label-text">Firma</label>
              <select value={mandantId} onChange={e => { setMandantId(e.target.value); }}
                className="input-field text-sm">
                <option value="">— Firma wählen —</option>
                {mandanten.map(m => (
                  <option key={m.id} value={m.id}>{m.kurz_code} — {m.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="label-text">Titel</label>
              <input value={titel} onChange={e => { setTitel(e.target.value); }}
                placeholder="z.B. Rechnung Bürobedarf" className="input-field text-sm" />
            </div>
            {typ === 'BELEG' && (
              <div className="space-y-1">
                <label className="label-text">Betrag (€, brutto)</label>
                <input value={betrag} onChange={e => { setBetrag(e.target.value); }}
                  inputMode="decimal" placeholder="z.B. 142,50" className="input-field text-sm" />
              </div>
            )}
            <div className="space-y-1">
              <label className="label-text">Notiz / Zusammenfassung</label>
              <textarea value={inhalt} onChange={e => { setInhalt(e.target.value); }}
                rows={3} className="input-field text-sm" />
            </div>
            <div className="space-y-1">
              <label className="label-text">🎯 Nächster Schritt {kiWar && <span className="text-blue-500">(KI-Vorschlag)</span>}</label>
              <input value={schritt2} onChange={e => { setSchritt2(e.target.value); }}
                placeholder="z.B. Zur Steuerberater-Ausgabe vormerken"
                className="input-field text-sm" />
              <p className="text-xs text-gray-400">
                Erscheint in der Akte als offene Aufgabe (abhakbar). Leer lassen = keine Aufgabe.
              </p>
            </div>
            <button onClick={() => { void handleSave(); }}
              disabled={saving || mandantId === '' || titel.trim() === ''}
              className="btn-primary w-full justify-center disabled:opacity-50">
              {saving ? 'Speichere…' : '✓ Freigeben & in Akte ablegen'}
            </button>
            {mandantId === '' && (
              <p className="text-xs text-amber-600 text-center">Bitte eine Firma wählen.</p>
            )}
          </div>
        )}

        {schritt === 'fertig' && (
          <div className="space-y-3">
            <p className="text-center py-2 text-green-600 dark:text-green-400 font-medium">
              ✓ In der Firmen-Akte abgelegt
            </p>
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500">Weiter bearbeiten:</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={öffneBegehung} className="btn-secondary text-sm justify-start">
                  🔍 Begehung
                </button>
                {[
                  { typ: 'ASA-Protokoll',       icon: '🛡' },
                  { typ: 'Befundbrief',         icon: '📝' },
                  { typ: 'Eignungszeugnis',     icon: '✅' },
                ].map(f => (
                  <button key={f.typ} onClick={() => { öffneFormular(f.typ); }}
                    className="btn-secondary text-sm justify-start">
                    {f.icon} {f.typ}
                  </button>
                ))}
              </div>
            </div>
            {isAnthropicConfigured() && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500">Stammdaten anlegen:</p>
                <button onClick={() => { void alsMandant(); }} disabled={stammBusy !== null}
                  className="btn-secondary text-sm w-full justify-start disabled:opacity-50">
                  {stammBusy === 'mandant' ? '🏢 Lese Firmendaten…' : '🏢 Als Mandant anlegen'}
                </button>
                <button onClick={() => { void alsMitarbeiter(); }} disabled={stammBusy !== null}
                  className="btn-secondary text-sm w-full justify-start disabled:opacity-50">
                  {stammBusy === 'mitarbeiter' ? '👤 Lese Personendaten…' : '👤 Als Mitarbeiter anlegen'}
                </button>
              </div>
            )}
            <button onClick={onClose} className="btn-primary w-full justify-center">
              Fertig
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
